import { useMemo, useState } from "react";
import { useAdminProducts } from "@/hooks/useAdminStore";
import AdminHeader from "@/pages/admin/components/AdminHeader";

type LinkTab = "products" | "offers";

const MARKETPLACE_COLORS: Record<string, string> = {
  Amazon: "bg-amber-100 text-amber-700",
  "Mercado Livre": "bg-yellow-100 text-yellow-700",
  Shopee: "bg-orange-100 text-orange-700",
  "Magazine Luiza": "bg-blue-100 text-blue-700",
  Americanas: "bg-red-100 text-red-700",
  Kabum: "bg-purple-100 text-purple-700",
};

export default function AdminLinks() {
  const { products, updateProduct } = useAdminProducts();
  const [tab, setTab] = useState<LinkTab>("products");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingLink, setEditingLink] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const offerProducts = useMemo(
    () => products.filter((product) => product.showInDailyDeals),
    [products]
  );

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const startEdit = (id: number, currentLink: string) => {
    setEditingId(id);
    setEditingLink(currentLink === "#" ? "" : currentLink);
  };

  const saveLink = (id: number) => {
    updateProduct(id, { affiliateLink: editingLink || "#" });
    setEditingId(null);
    showToast("Link atualizado com sucesso!");
  };

  const baseItems = tab === "products" ? products : offerProducts;
  const items = baseItems.filter((item) => item.name.toLowerCase().includes(search.toLowerCase()));
  const withLink = items.filter((item) => item.affiliateLink && item.affiliateLink !== "#").length;
  const withoutLink = items.length - withLink;

  return (
    <div>
      <AdminHeader
        title="Links de Afiliado"
        subtitle="Produtos e Ofertas do Dia usam o mesmo link salvo no cadastro do produto"
      />

      <div className="mb-6 rounded-2xl border border-orange-100 bg-orange-50 px-5 py-4">
        <p className="text-sm font-semibold text-orange-700">Ofertas do Dia espelham o cadastro do produto</p>
        <p className="text-sm text-orange-600 mt-0.5">
          Ao editar o link na aba de ofertas, você está atualizando o mesmo link do produto cadastrado.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <i className="ri-links-line text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{withLink}</p>
            <p className="text-xs text-gray-500">Com link</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-500">
            <i className="ri-link-unlink-m text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{withoutLink}</p>
            <p className="text-xs text-gray-500">Sem link</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4 flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-50 text-orange-600">
            <i className="ri-percent-line text-lg"></i>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">
              {items.length > 0 ? Math.round((withLink / items.length) * 100) : 0}%
            </p>
            <p className="text-xs text-gray-500">Cobertura</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-5">
        <div className="flex bg-gray-100 rounded-xl p-1">
          {([
            { id: "products", label: "Produtos" },
            { id: "offers", label: "Ofertas do Dia" },
          ] as const).map((item) => (
            <button
              key={item.id}
              onClick={() => {
                setTab(item.id);
                setSearch("");
                setEditingId(null);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                tab === item.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Produto</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Marketplace</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide w-1/2">Link de Afiliado</th>
              <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ação</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="text-center py-12 text-gray-400 text-sm">
                  Nenhum item encontrado
                </td>
              </tr>
            )}
            {items.map((item) => {
              const hasLink = item.affiliateLink && item.affiliateLink !== "#";
              const isEditing = editingId === item.id;

              return (
                <tr key={`${tab}-${item.id}`} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <img src={item.image} alt={item.name} className="w-9 h-9 rounded-lg object-cover flex-shrink-0 bg-gray-100" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate max-w-[220px]">{item.name}</p>
                        {tab === "offers" && (
                          <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                            <i className="ri-fire-line"></i>
                            Oferta do Dia
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${MARKETPLACE_COLORS[item.marketplace] || "bg-gray-100 text-gray-600"}`}>
                      {item.marketplace}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    {isEditing ? (
                      <div className="flex items-center gap-2">
                        <input
                          autoFocus
                          value={editingLink}
                          onChange={(e) => setEditingLink(e.target.value)}
                          placeholder="https://amzn.to/xxxxx"
                          className="flex-1 px-3 py-1.5 rounded-lg border border-orange-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-100"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveLink(item.id);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                        <button onClick={() => saveLink(item.id)} className="px-3 py-1.5 bg-orange-500 text-white text-xs font-bold rounded-lg hover:bg-orange-600 cursor-pointer whitespace-nowrap">
                          Salvar
                        </button>
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 border border-gray-200 text-gray-500 text-xs font-semibold rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {hasLink ? (
                          <>
                            <i className="ri-checkbox-circle-fill text-emerald-500 flex-shrink-0"></i>
                            <a
                              href={item.affiliateLink}
                              target="_blank"
                              rel="nofollow noopener noreferrer"
                              className="text-sm text-gray-600 hover:text-orange-500 truncate max-w-xs cursor-pointer"
                            >
                              {item.affiliateLink}
                            </a>
                          </>
                        ) : (
                          <>
                            <i className="ri-error-warning-line text-red-400 flex-shrink-0"></i>
                            <span className="text-sm text-gray-400 italic">Link não definido</span>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    {!isEditing && (
                      <button
                        onClick={() => startEdit(item.id, item.affiliateLink)}
                        className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-orange-50 hover:text-orange-500 hover:border-orange-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-edit-line mr-1"></i>
                        {hasLink ? "Editar" : "Adicionar"}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl z-50">
          <i className="ri-checkbox-circle-line text-green-400 mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}
